import React, { createContext, useContext, useRef } from "react";
import { Box, Dialog } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, FONT_DISPLAY, RADIUS } from "../../utils/designTokens";

// The site's popup: a white card over a dimmed, lightly blurred page, a round
// close button top-right, a Bricolage title, Inter text and full-width stacked
// buttons. First drawn for the "restaurant closed" popup; every small message
// or confirm popup uses it so they all look and behave alike.
//
//   <RmfDialog open={open} onClose={close}>
//     <DialogIcon tone="gold"><SomeIcon /></DialogIcon>
//     <DialogTitle>Clear your cart?</DialogTitle>
//     <DialogText>Your items from Saigon Street Kitchen will be removed.</DialogText>
//     <DialogActions>
//       <Box component="button" type="button" sx={dialogButtonSx.primary}>Clear cart</Box>
//       <Box component="button" type="button" sx={dialogButtonSx.secondary}>Keep my cart</Box>
//     </DialogActions>
//   </RmfDialog>
//
// MUI's Dialog keeps focus inside while open, returns it on close and closes
// on Esc; the title is wired to the dialog's aria-labelledby.

const TitleIdContext = createContext(null);
let nextId = 0;

export const DIALOG_BACKDROP_SX = {
  backgroundColor: "rgba(26,25,24,0.55)",
  backdropFilter: "blur(3px)",
  WebkitBackdropFilter: "blur(3px)",
};

export const DIALOG_SHADOW = "0 24px 60px rgba(0,0,0,0.30)";

export default function RmfDialog({ open, onClose, children, maxWidth = 480, closeLabel = "Close", hideClose = false, paperSx, ...rest }) {
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = `rmf-dialog-title-${++nextId}`;
  const titleId = idRef.current;
  // Focus the dialog itself on open (MUI focuses its outer wrapper), so a
  // screen reader announces the title; Tab then moves through the buttons.
  const paperRef = useRef(null);
  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby={titleId}
      TransitionProps={{ onEntering: () => paperRef.current?.focus({ preventScroll: true }) }}
      PaperProps={{
        ref: paperRef,
        tabIndex: -1,
        "aria-modal": true,
        sx: {
          "&:focus": { outline: "none" },
          position: "relative",
          width: "calc(100% - 32px)",
          maxWidth,
          m: 2,
          p: { xs: 3, sm: 4 },
          boxSizing: "border-box",
          bgcolor: C.bg,
          borderRadius: "24px",
          boxShadow: DIALOG_SHADOW,
          fontFamily: FONT_BODY,
          ...paperSx,
        },
      }}
      {...rest}
    >
      {!hideClose && (
        <Box
          component="button"
          type="button"
          aria-label={closeLabel}
          onClick={(e) => onClose?.(e, "closeButton")}
          sx={{
            ...BUTTON_RESET,
            ...FOCUS_RING,
            position: "absolute",
            top: { xs: 16, sm: 20 },
            right: { xs: 16, sm: 20 },
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: C.surface2,
            color: C.ink,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background-color .15s ease",
            "&:hover": { bgcolor: C.border },
          }}
        >
          <CloseRoundedIcon aria-hidden="true" sx={{ fontSize: 22 }} />
        </Box>
      )}
      <TitleIdContext.Provider value={titleId}>{children}</TitleIdContext.Provider>
    </Dialog>
  );
}

/** The popup's heading. `besideClose` keeps a title on the first row clear of the close button. */
export function DialogTitle({ children, besideClose = true, sx }) {
  const titleId = useContext(TitleIdContext);
  return (
    <Box
      component="h2"
      id={titleId}
      sx={{
        fontFamily: FONT_DISPLAY,
        fontSize: 26,
        fontWeight: 800,
        lineHeight: 1.15,
        letterSpacing: "-0.02em",
        color: C.ink,
        m: 0,
        pr: besideClose ? 6 : 0,
        overflowWrap: "anywhere",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export function DialogText({ children, sx }) {
  return (
    <Box component="p" sx={{ fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.55, color: C.muted, m: 0, mt: 1.25, ...sx }}>
      {children}
    </Box>
  );
}

const TONES = {
  gold: { color: C.gold, bg: C.goldSoft },
  red: { color: C.red, bg: C.redTint },
  green: { color: C.green, bg: C.greenSoft },
  neutral: { color: C.ink, bg: C.surface2 },
};

/** 56px rounded-square icon tile that opens a popup. */
export function DialogIcon({ tone = "neutral", children, sx }) {
  const t = TONES[tone] || TONES.neutral;
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 56,
        height: 56,
        flexShrink: 0,
        borderRadius: "16px",
        bgcolor: t.bg,
        color: t.color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& svg": { fontSize: 28 },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** Small status chip with a coloured dot, e.g. "Closed · opens 11:00 AM". */
export function DialogChip({ children, dot = C.goldAccent, sx }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        height: 30,
        px: 1.5,
        borderRadius: RADIUS.pill,
        bgcolor: C.surface2,
        color: C.ink,
        fontFamily: FONT_BODY,
        fontSize: 13,
        fontWeight: 600,
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: dot, flexShrink: 0 }} />
      {children}
    </Box>
  );
}

/** Full-width buttons, stacked. */
export function DialogActions({ children, sx }) {
  return <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 3, ...sx }}>{children}</Box>;
}

const buttonBase = {
  ...BUTTON_RESET,
  ...FOCUS_RING,
  boxSizing: "border-box",
  width: "100%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  fontFamily: FONT_BODY,
  fontSize: 16,
  fontWeight: 700,
  textAlign: "center",
  textDecoration: "none",
  textTransform: "none",
  borderRadius: RADIUS.controlLg,
  transition: "background-color .15s ease, border-color .15s ease, color .15s ease",
  "&:disabled": { cursor: "not-allowed", bgcolor: C.disabledBg, color: C.disabledText, borderColor: C.disabledBg },
  "& svg": { fontSize: 20 },
};

// Use with <Box component="button" type="button"> or <Box component={RouterLink}>.
export const dialogButtonSx = {
  primary: {
    ...buttonBase,
    minHeight: 56,
    px: 2.5,
    bgcolor: C.red,
    color: "#FFFFFF",
    "&:hover:not(:disabled)": { bgcolor: C.redHover },
  },
  // Same weight, for a destructive or second-choice action that still needs to stand out.
  secondary: {
    ...buttonBase,
    minHeight: 52,
    px: 2.5,
    bgcolor: C.bg,
    color: C.ink,
    border: `1.5px solid ${C.borderStrong}`,
    "&:hover:not(:disabled)": { borderColor: C.ink, bgcolor: C.surface },
  },
  link: {
    ...buttonBase,
    width: "auto",
    alignSelf: "center",
    minHeight: 40,
    px: 1,
    color: C.redLink,
    fontSize: 15,
    fontWeight: 600,
    borderRadius: RADIUS.control,
    "&:hover": { textDecoration: "underline" },
  },
};
